import React, { useEffect, useState, useRef } from "react";
import api, { fcfa, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Store, Package, Tag, Crown, Upload, Trash2, Plus, MapPin, Camera, Paperclip, X } from "lucide-react";

export default function MerchantDashboard() {
  const { user } = useAuth();
  const [shop, setShop] = useState(null);
  const [shopForm, setShopForm] = useState({ name: "", description: "", category: "", address: "", city: "", phone: "", hours: "", lat: "", lng: "", logo_url: "" });
  const [products, setProducts] = useState([]);
  const [promos, setPromos] = useState([]);
  const [sub, setSub] = useState(null);
  const [pForm, setPForm] = useState({ name: "", category: "", price: "", unit: "unité", description: "", image_url: "" });
  const [promoForm, setPromoForm] = useState({ title: "", description: "", discount: "", product_name: "", end_date: "" });
  const [openP, setOpenP] = useState(false);
  const [openPromo, setOpenPromo] = useState(false);
  const fileRef = useRef();

  const load = () => {
    api.get("/merchant/shop").then((r) => { setShop(r.data); if (r.data) { setShopForm((f) => ({ ...f, ...r.data })); setLogoPreview(r.data.logo_url || ""); } });
    api.get("/merchant/products").then((r) => setProducts(r.data));
    api.get("/merchant/promotions").then((r) => setPromos(r.data));
    api.get("/subscriptions/me").then((r) => setSub(r.data));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const saveShop = async () => {
    try {
      const payload = { ...shopForm, lat: shopForm.lat ? parseFloat(shopForm.lat) : null, lng: shopForm.lng ? parseFloat(shopForm.lng) : null };
      const { data } = await api.post("/merchant/shop", payload);
      setShop(data); toast.success("Boutique enregistrée !");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addProduct = async () => {
    if (!pForm.name || !pForm.price || !pForm.category) { toast.error("Nom, prix et catégorie requis"); return; }
    try { await api.post("/merchant/products", { ...pForm, price: parseFloat(pForm.price) }); toast.success("Produit ajouté"); setOpenP(false); setPForm({ name: "", category: "", price: "", unit: "unité", description: "", image_url: "" }); setProdImgPreview(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delProduct = async (id) => { await api.delete(`/merchant/products/${id}`); load(); };

  const addPromo = async () => {
    try { await api.post("/merchant/promotions", { ...promoForm, start_date: new Date().toISOString().slice(0, 10) }); toast.success("Promotion créée"); setOpenPromo(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delPromo = async (id) => { await api.delete(`/merchant/promotions/${id}`); load(); };

  const importFile = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/merchant/products/import", fd); toast.success(`${data.imported} produits importés`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const subscribe = async () => {
    const { data } = await api.post("/payments/initiate", { kind: "abonnement", amount: 15000, plan: "pro_commercant" });
    window.location.href = `/paiement/${data.tx_id}`;
  };

  const set = (k) => (e) => setShopForm({ ...shopForm, [k]: e.target.value });

  const detectGPS = () => {
    if (!navigator.geolocation) { toast.error("Géolocalisation non disponible"); return; }
    toast.info("Détection en cours…");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setShopForm((f) => ({ ...f, lat: pos.coords.latitude.toFixed(6), lng: pos.coords.longitude.toFixed(6) }));
        toast.success("Coordonnées détectées !");
      },
      () => toast.error("Impossible de détecter la position")
    );
  };

  const logoFileRef = useRef();
  const logoCamRef = useRef();
  const [logoPreview, setLogoPreview] = useState(shopForm.logo_url || "");

  const prodImgFileRef = useRef();
  const prodImgCamRef = useRef();
  const [prodImgPreview, setProdImgPreview] = useState("");

  const handleProdImgFile = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/merchant/upload", fd);
      const url = data.url || data.file_url || "";
      setPForm((f) => ({ ...f, image_url: url }));
      setProdImgPreview(url);
      toast.success("Image téléchargée !");
    } catch {
      const reader = new FileReader();
      reader.onload = (ev) => { setProdImgPreview(ev.target.result); setPForm((f) => ({ ...f, image_url: ev.target.result })); };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoFile = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/merchant/upload", fd);
      const url = data.url || data.logo_url || data.file_url || "";
      setShopForm((f) => ({ ...f, logo_url: url }));
      setLogoPreview(url);
      toast.success("Logo téléchargé !");
    } catch {
      // fallback: prévisualisation locale uniquement
      const reader = new FileReader();
      reader.onload = (ev) => { setLogoPreview(ev.target.result); setShopForm((f) => ({ ...f, logo_url: ev.target.result })); };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-secondary">Espace commerçant</h1>
          <p className="text-muted-foreground">{shop?.name || "Configurez votre boutique pour commencer."}</p>
        </div>
        {user.status === "pending" && <Badge className="bg-amber-500 text-white">Compte en attente de validation</Badge>}
        {sub?.status === "active" ? <Badge className="bg-accent text-accent-foreground"><Crown className="w-3 h-3 mr-1" />Pro actif</Badge> : null}
      </div>

      <Tabs defaultValue="shop" className="mt-6">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="shop" data-testid="tab-shop"><Store className="w-4 h-4 mr-1" />Boutique</TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products"><Package className="w-4 h-4 mr-1" />Produits</TabsTrigger>
          <TabsTrigger value="promos" data-testid="tab-promos"><Tag className="w-4 h-4 mr-1" />Promotions</TabsTrigger>
          <TabsTrigger value="sub" data-testid="tab-sub"><Crown className="w-4 h-4 mr-1" />Abonnement</TabsTrigger>
        </TabsList>

        <TabsContent value="shop" className="mt-4">
          <div className="bg-card border border-border rounded-xl p-6 grid sm:grid-cols-2 gap-4">
            <div><Label>Nom *</Label><Input data-testid="shop-name" value={shopForm.name} onChange={set("name")} /></div>
            <div><Label>Catégorie</Label><Input value={shopForm.category} onChange={set("category")} /></div>
            <div className="sm:col-span-2"><Label>Description</Label><Textarea value={shopForm.description} onChange={set("description")} /></div>
            <div><Label>Ville</Label><Input value={shopForm.city} onChange={set("city")} /></div>
            <div><Label>Adresse</Label><Input value={shopForm.address} onChange={set("address")} /></div>
            <div><Label>Téléphone</Label><Input value={shopForm.phone} onChange={set("phone")} /></div>
            <div><Label>Horaires</Label><Input value={shopForm.hours} onChange={set("hours")} placeholder="Lun-Sam 8h-20h" /></div>
            {/* GPS */}
            <div>
              <Label>Latitude</Label>
              <Input value={shopForm.lat} onChange={set("lat")} placeholder="5.359" />
            </div>
            <div>
              <Label>Longitude</Label>
              <div className="flex gap-2">
                <Input value={shopForm.lng} onChange={set("lng")} placeholder="-3.999" />
                <Button type="button" variant="outline" onClick={detectGPS} title="Détecter ma position GPS" className="shrink-0 px-3">
                  <MapPin className="w-4 h-4 text-primary" />
                </Button>
              </div>
            </div>
            {/* Logo avec caméra / fichier */}
            <div className="sm:col-span-2">
              <Label>Logo / Photo</Label>
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                {logoPreview ? (
                  <div className="relative w-16 h-16 rounded-lg overflow-hidden border border-border shrink-0">
                    <img src={logoPreview} alt="logo" className="w-full h-full object-cover" />
                    <button onClick={() => { setLogoPreview(""); setShopForm((f) => ({ ...f, logo_url: "" })); }}
                      className="absolute top-0.5 right-0.5 bg-black/50 rounded-full p-0.5 text-white">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="w-16 h-16 rounded-lg border-2 border-dashed border-border flex items-center justify-center text-muted-foreground shrink-0">
                    <Camera className="w-6 h-6" />
                  </div>
                )}
                <div className="flex gap-2 flex-wrap">
                  {/* Prendre une photo avec la caméra */}
                  <Button type="button" variant="outline" size="sm" onClick={() => logoCamRef.current?.click()}>
                    <Camera className="w-4 h-4 mr-1" />Photo
                  </Button>
                  <input ref={logoCamRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleLogoFile} />
                  {/* Joindre depuis l'appareil */}
                  <Button type="button" variant="outline" size="sm" onClick={() => logoFileRef.current?.click()}>
                    <Paperclip className="w-4 h-4 mr-1" />Fichier
                  </Button>
                  <input ref={logoFileRef} type="file" accept="image/*" className="hidden" onChange={handleLogoFile} />
                </div>
                <Input className="flex-1 min-w-[160px]" value={shopForm.logo_url} onChange={(e) => { set("logo_url")(e); setLogoPreview(e.target.value); }} placeholder="ou collez une URL…" />
              </div>
            </div>
            <div className="sm:col-span-2"><Button data-testid="save-shop" className="bg-primary hover:bg-primary/90" onClick={saveShop}>Enregistrer</Button></div>
          </div>
        </TabsContent>

        <TabsContent value="products" className="mt-4">
          <div className="flex gap-2 mb-4">
            <Dialog open={openP} onOpenChange={setOpenP}>
              <DialogTrigger asChild><Button data-testid="add-product" className="bg-primary hover:bg-primary/90"><Plus className="w-4 h-4 mr-1" />Ajouter</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Nouveau produit</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Nom *</Label><Input data-testid="prod-name" value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Prix (FCFA) *</Label><Input data-testid="prod-price" type="number" value={pForm.price} onChange={(e) => setPForm({ ...pForm, price: e.target.value })} /></div>
                    <div><Label>Unité</Label><Input value={pForm.unit} onChange={(e) => setPForm({ ...pForm, unit: e.target.value })} /></div>
                  </div>
                  <div><Label>Catégorie *</Label><Input data-testid="prod-category" value={pForm.category} onChange={(e) => setPForm({ ...pForm, category: e.target.value })} /></div>
                  <div>
                    <Label>Image du produit</Label>
                    <div className="flex gap-2 mt-1">
                      <input ref={prodImgCamRef} type="file" accept="image/*" capture="environment" hidden onChange={handleProdImgFile} />
                      <input ref={prodImgFileRef} type="file" accept="image/*" hidden onChange={handleProdImgFile} />
                      <Button type="button" size="icon" variant="outline" title="Prendre une photo" onClick={() => prodImgCamRef.current.click()}><Camera className="w-4 h-4" /></Button>
                      <Button type="button" size="icon" variant="outline" title="Choisir un fichier" onClick={() => prodImgFileRef.current.click()}><Paperclip className="w-4 h-4" /></Button>
                      <Input data-testid="prod-image" placeholder="ou coller une URL" value={pForm.image_url} onChange={(e) => { setPForm({ ...pForm, image_url: e.target.value }); setProdImgPreview(e.target.value); }} className="flex-1" />
                    </div>
                    {prodImgPreview && (
                      <div className="relative inline-block mt-2">
                        <img src={prodImgPreview} alt="aperçu" className="h-20 w-20 object-cover rounded border border-border" />
                        <button type="button" className="absolute -top-1 -right-1 bg-destructive text-white rounded-full w-4 h-4 flex items-center justify-center text-xs" onClick={() => { setProdImgPreview(""); setPForm((f) => ({ ...f, image_url: "" })); }}><X className="w-3 h-3" /></button>
                      </div>
                    )}
                  </div>
                </div>
                <DialogFooter><Button data-testid="save-product" className="bg-primary hover:bg-primary/90" onClick={addProduct}>Ajouter</Button></DialogFooter>
              </DialogContent>
            </Dialog>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" hidden onChange={importFile} />
            <Button data-testid="import-btn" variant="outline" onClick={() => fileRef.current.click()}><Upload className="w-4 h-4 mr-1" />Importer CSV/Excel</Button>
          </div>
          <div className="bg-card border border-border rounded-xl divide-y divide-border">
            {products.length === 0 && <div className="p-6 text-center text-muted-foreground text-sm">Aucun produit. Ajoutez-en ou importez un fichier (colonnes: name, price, category).</div>}
            {products.map((p) => (
              <div key={p.product_id} className="p-4 flex items-center justify-between">
                <div><div className="font-medium text-secondary">{p.name}</div><div className="text-xs text-muted-foreground">{p.category} · {p.unit}</div></div>
                <div className="flex items-center gap-3">
                  <span className="font-display font-bold text-primary">{fcfa(p.price)}</span>
                  <Button size="icon" variant="ghost" data-testid={`del-prod-${p.product_id}`} onClick={() => delProduct(p.product_id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="promos" className="mt-4">
          <Dialog open={openPromo} onOpenChange={setOpenPromo}>
            <DialogTrigger asChild><Button data-testid="add-promo" className="bg-primary hover:bg-primary/90 mb-4"><Plus className="w-4 h-4 mr-1" />Nouvelle promotion</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nouvelle promotion</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Titre *</Label><Input data-testid="promo-title" value={promoForm.title} onChange={(e) => setPromoForm({ ...promoForm, title: e.target.value })} /></div>
                <div><Label>Réduction</Label><Input value={promoForm.discount} onChange={(e) => setPromoForm({ ...promoForm, discount: e.target.value })} placeholder="-20%" /></div>
                <div><Label>Description</Label><Textarea value={promoForm.description} onChange={(e) => setPromoForm({ ...promoForm, description: e.target.value })} /></div>
                <div><Label>Fin de validité</Label><Input type="date" value={promoForm.end_date} onChange={(e) => setPromoForm({ ...promoForm, end_date: e.target.value })} /></div>
              </div>
              <DialogFooter><Button data-testid="save-promo" className="bg-primary hover:bg-primary/90" onClick={addPromo}>Publier</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          <div className="grid sm:grid-cols-2 gap-4">
            {promos.map((p) => (
              <div key={p.promo_id} className="bg-card border border-border rounded-lg p-4 flex justify-between">
                <div><div className="font-semibold text-secondary">{p.title} <span className="text-primary">{p.discount}</span></div><div className="text-xs text-muted-foreground">Jusqu'au {p.end_date || "—"}</div></div>
                <Button size="icon" variant="ghost" onClick={() => delPromo(p.promo_id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="sub" className="mt-4">
          <div className="bg-card border border-border rounded-xl p-8 max-w-md">
            <Crown className="w-10 h-10 text-primary mb-3" />
            <h3 className="font-display text-xl font-bold text-secondary">Abonnement Pro Commerçant</h3>
            <div className="font-display text-3xl font-extrabold text-primary my-3">{fcfa(15000)}<span className="text-sm text-muted-foreground font-normal">/mois</span></div>
            <ul className="text-sm text-muted-foreground space-y-1 mb-5">
              <li>✓ Mise en avant de la boutique</li><li>✓ Badge Pro vérifié</li><li>✓ Produits illimités & import fichier</li><li>✓ Statistiques avancées</li>
            </ul>
            {sub?.status === "active"
              ? <Badge className="bg-accent text-accent-foreground">Abonnement actif jusqu'au {sub.end?.slice(0, 10)}</Badge>
              : <Button data-testid="subscribe-btn" className="w-full bg-primary hover:bg-primary/90" onClick={subscribe}>S'abonner via Mobile Money</Button>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
